import { PDFDocument, PDFName } from "pdf-lib"

export type PdfBoxSizeInches = {
  width: number
  height: number
}

export const getPdfBytes = async (
  source: string | File[] | Uint8Array,
): Promise<Uint8Array> => {
  if (typeof source === "string") {
    const response = await fetch(source)
    if (!response.ok) {
      throw new Error(`Failed to fetch PDF (${response.status})`)
    }
    return new Uint8Array(await response.arrayBuffer())
  }

  if (source instanceof Uint8Array) {
    return source
  }

  if (!source.length) {
    throw new Error("PDF source is empty")
  }

  return new Uint8Array(await source[0].arrayBuffer())
}

export type PdfBoxName = "TrimBox" | "BleedBox" | "ArtBox"

export const hasPdfBox = (page: unknown, boxName: PdfBoxName): boolean => {
  const pageNode = (
    page as {
      node?: {
        get?: (name: unknown) => unknown
        TrimBox?: () => unknown
        BleedBox?: () => unknown
        ArtBox?: () => unknown
      }
    }
  ).node

  if (!pageNode) {
    return false
  }

  if (boxName === "TrimBox" && typeof pageNode.TrimBox === "function") {
    return Boolean(pageNode.TrimBox())
  }
  if (boxName === "BleedBox" && typeof pageNode.BleedBox === "function") {
    return Boolean(pageNode.BleedBox())
  }
  if (boxName === "ArtBox" && typeof pageNode.ArtBox === "function") {
    return Boolean(pageNode.ArtBox())
  }

  return Boolean(pageNode.get?.(PDFName.of(boxName)))
}

const PDF_DEFAULT_DPI = 72
export const pointsToInches = (value: number, dpi = PDF_DEFAULT_DPI) =>
  value / dpi

export const inchesToPoints = (value: number, dpi = PDF_DEFAULT_DPI) =>
  value * dpi

export type PdfTrimInsetsPoints = {
  /** TrimBox left edge from the MediaBox left edge, in points (≥ 0). */
  left: number
  /** TrimBox top edge from the MediaBox TOP edge, in points (≥ 0). */
  top: number
  /** First page's MediaBox size in points. */
  mediaWidth: number
  mediaHeight: number
}

/**
 * Where the first page's TrimBox sits inside its MediaBox, in PDF points.
 *
 * PDF coordinates are bottom-left-origin, but PDF importers (and Polotno)
 * emit top-left-origin geometry relative to the MediaBox — so `top` here is
 * measured from the media box's TOP edge, ready to subtract from imported
 * coordinates to re-origin content at the trim corner.
 *
 * When the PDF declares no TrimBox, a trim of `fallbackTrimSizeInches`
 * centered in the MediaBox is assumed (a full-bleed export whose media IS the
 * bleed box, or a trim-sized export where the insets come out 0). Insets are
 * clamped at 0 so a malformed box can't shift content outward.
 */
export const getPdfTrimInsetsPoints = async (
  source: string | File[] | Uint8Array,
  fallbackTrimSizeInches: PdfBoxSizeInches,
): Promise<PdfTrimInsetsPoints> => {
  const bytes = await getPdfBytes(source)
  const doc = await PDFDocument.load(bytes)
  const page = doc.getPage(0)
  const media = page.getMediaBox()

  if (hasPdfBox(page, "TrimBox")) {
    const trim = page.getTrimBox()
    return {
      left: Math.max(0, trim.x - media.x),
      top: Math.max(0, media.y + media.height - (trim.y + trim.height)),
      mediaWidth: media.width,
      mediaHeight: media.height,
    }
  }

  return {
    left: Math.max(
      0,
      (media.width - inchesToPoints(fallbackTrimSizeInches.width)) / 2,
    ),
    top: Math.max(
      0,
      (media.height - inchesToPoints(fallbackTrimSizeInches.height)) / 2,
    ),
    mediaWidth: media.width,
    mediaHeight: media.height,
  }
}
